import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  Minus,
  Plus,
} from 'lucide-react';

import CardBlock from '@/components/CardBlock';
import InlineEdit from '@/components/InlineEdit';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';

const defaultClause = {
  id: +new Date(),
  formData: {},
  rows: [],
  title: '',
  expanded: true,
};

const SortableCardWrapper = ({ id, children }) => {
  const { setNodeRef, attributes, listeners, transform, transition } =
    useSortable({ id });
    
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

const ClauseView = ({ document, setDocument }) => {
  const { mode } = useDetailMode(DETAIL_TYPE.DOCUMENT);

  const clauses = document?.clause?.clauses || [defaultClause];

  const sensors = useSensors(useSensor(PointerSensor));

  const updateClauseData = (index, newPartial) => {
    setDocument((prev) => {
      const newClauses = [...(prev.clause?.clauses || [defaultClause])];

      newClauses[index] = {
        ...newClauses[index],
        ...newPartial,
      };
      return {
        ...prev,
        clause: {
          ...prev.clause,
          clauses: newClauses,
        },
      };
    });
  };

  const handleTitleChange = (index, value) => {
    updateClauseData(index, { title: value });
  };

  const handleAddTextClick = (index) => {
    const newSections = [
      ...clauses[index].rows,
      {
        id: +new Date(),
        type: 'textarea',
        // type: 'multi-text',
      },
    ];

    updateClauseData(index, { rows: newSections });
  };

  const handleAddFileClick = (index) => {
    const newSections = [
      ...clauses[index].rows,
      {
        id: +new Date(),
        type: 'file',
      },
    ];

    updateClauseData(index, { rows: newSections });
  };

  const handleAddTableClick = (index) => {
    const newSections = [
      ...clauses[index].rows,
      {
        id: +new Date(),
        type: 'table',
      },
    ];

    updateClauseData(index, { rows: newSections });
  };

  const handleAddClause = () => {
    setDocument((prev) => {
      const newClauses = [
        ...(prev.clause?.clauses || [defaultClause]),
        { ...defaultClause, id: +new Date() },
      ];

      return {
        ...prev,
        clause: {
          ...prev.clause,
          clauses: newClauses,
        },
      };
    });
  };

  const handleRemoveClause = (index) => {
    setDocument((prev) => {
      const newClauses = [...(prev.clause?.clauses || [defaultClause])];

      if (newClauses.length > 1) {
        newClauses.splice(index, 1);
      }

      return {
        ...prev,
        clause: {
          ...prev.clause,
          clauses: newClauses,
        },
      };
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = clauses.findIndex((c) => c.id === active.id);
    const newIndex = clauses.findIndex((c) => c.id === over.id);
    const newClauses = arrayMove(clauses, oldIndex, newIndex);

    setDocument((prev) => ({
      ...prev,
      clause: {
        ...prev.clause,
        clauses: newClauses,
      },
    }));
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={clauses.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-6">
            {clauses.map(
              ({ formData, rows, title, id, expanded }, clauseIndex) => (
                <SortableCardWrapper key={id} id={id}>
                  {({ dragHandleProps }) => (
                    <Collapsible
                      open={expanded}
                      onOpenChange={(val) =>
                        updateClauseData(clauseIndex, { expanded: val })
                      }
                    >
                      <Card>
                        <CardHeader className="pb-6">
                          <div className="flex items-center justify-between">
                            {mode !== DETAIL_MODE.STATIC && (
                              <Button
                                className="-ml-4"
                                variant="ghost"
                                size="icon"
                                {...dragHandleProps}
                              >
                                <GripVertical />
                              </Button>
                            )}
                            <CardTitle className="grow text-base font-medium">
                              <InlineEdit
                                className="md:text-base"
                                mode={mode}
                                value={title || ''}
                                onChange={(val) =>
                                  handleTitleChange(clauseIndex, val)
                                }
                                placeholder="Add Clause Title"
                              />
                            </CardTitle>
                            <div>
                              {clauses.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleRemoveClause(clauseIndex)
                                  }
                                >
                                  <Minus />
                                </Button>
                              )}
                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  {expanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            </div>
                          </div>
                        </CardHeader>
                        <CollapsibleContent>
                          <CardContent className="pt-0">
                            {rows.map(({ id, type }) => (
                              <CardBlock
                                collapsible={false}
                                fieldPlaceholder="Add Clause Detail"
                                formData={formData}
                                hideDetailTitle={type === 'textarea'}
                                id={id}
                                key={id}
                                mode={mode}
                                sections={rows}
                                setFormData={(updater) =>
                                  updateClauseData(clauseIndex, {
                                    formData: updater(formData),
                                  })
                                }
                                setSections={(newSections) =>
                                  updateClauseData(clauseIndex, {
                                    rows: newSections,
                                  })
                                }
                                titlePlaceholder={
                                  type === 'file'
                                    ? 'Description for File/Image'
                                    : type === 'table'
                                      ? 'Description for Table'
                                      : 'Description for Detail'
                                }
                                type={type}
                              />
                            ))}
                            {mode !== DETAIL_MODE.STATIC && (
                              <div className="flex gap-4 mt-4">
                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    handleAddTextClick(clauseIndex)
                                  }
                                >
                                  <Plus />
                                  Add Text Section
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    handleAddFileClick(clauseIndex)
                                  }
                                >
                                  <Plus />
                                  Add File
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    handleAddTableClick(clauseIndex)
                                  }
                                >
                                  <Plus />
                                  Add Custom Table
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </CollapsibleContent>
                      </Card>
                    </Collapsible>
                  )}
                </SortableCardWrapper>
              )
            )}
          </div>
        </SortableContext>
      </DndContext>
      {mode !== DETAIL_MODE.STATIC && (
        <div className="mt-6">
          <Button onClick={handleAddClause}>
            <Plus />
            Add Clause
          </Button>
        </div>
      )}
    </>
  );
};

export default ClauseView;
